import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "@/components/ScrollToTop";
import ProtectedRoute from "@/components/ProtectedRoute";
import ConsentBanner from "@/components/ConsentBanner";

const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Jobs = lazy(() => import("./pages/Jobs"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Referrals = lazy(() => import("./pages/Referrals"));
const CommunityService = lazy(() => import("./pages/CommunityService"));
const Donate = lazy(() => import("./pages/Donate"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const DataProtection = lazy(() => import("./pages/DataProtection"));
const Resources = lazy(() => import("./pages/Resources"));
const Shop = lazy(() => import("./pages/Shop"));
const NotFound = lazy(() => import("./pages/NotFound"));
const RegisterNurse = lazy(() => import("./pages/nurse/Register"));
const NurseVerification = lazy(() => import("./pages/nurse/Verification"));
const NurseDashboard = lazy(() => import("./pages/nurse/Dashboard"));
const NurseBenefits = lazy(() => import("./pages/nurse/Benefits"));
const NurseTimesheet = lazy(() => import("./pages/nurse/Timesheet"));
const NurseCompliance = lazy(() => import("./pages/nurse/Compliance"));
const ClientDashboard = lazy(() => import("./pages/client/Dashboard"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminShifts = lazy(() => import("./pages/admin/Shifts"));
const AdminNurses = lazy(() => import("./pages/admin/Nurses"));
const AdminClients = lazy(() => import("./pages/admin/Clients"));
const AdminCompliance = lazy(() => import("./pages/admin/Compliance"));
const AdminJobs = lazy(() => import("./pages/admin/Jobs"));
const AdminNursesManage = lazy(() => import("./pages/admin/NursesManage"));
const AdminClientsManage = lazy(() => import("./pages/admin/ClientsManage"));
const FallsPrevention = lazy(() => import("./pages/articles/FallsPrevention"));
const UnderstandingDementia = lazy(() => import("./pages/articles/UnderstandingDementia"));
const NutritionHealthyAging = lazy(() => import("./pages/articles/NutritionHealthyAging"));
const ChildhoodAsthma = lazy(() => import("./pages/articles/ChildhoodAsthma"));
const MentalHealthAwareness = lazy(() => import("./pages/articles/MentalHealthAwareness"));
const MealPlanning = lazy(() => import("./pages/articles/MealPlanning"));
const ExerciseSeniors = lazy(() => import("./pages/articles/ExerciseSeniors"));
const ChildrensEmotionalDevelopment = lazy(() => import("./pages/articles/ChildrensEmotionalDevelopment"));
const ManagingStressAnxiety = lazy(() => import("./pages/articles/ManagingStressAnxiety"));

const App = () => (
  <HelmetProvider>
      <Toaster />
      <Sonner />

      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<main className="grid min-h-screen place-items-center"><p>Loading…</p></main>}>
        <Routes>
          {/* Public Pages */}
          <Route path="/" element={<Index />} />
          <Route path="/login/:role" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/jobs" element={<Jobs />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/referrals" element={<Referrals />} />
          <Route path="/community-service" element={<CommunityService />} />
          <Route path="/donate" element={<Donate />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/data-protection" element={<DataProtection />} />

          {/* Registration */}
          <Route path="/register/nurse" element={<RegisterNurse />} />
          <Route path="/nurse/verification" element={<ProtectedRoute roles={["nurse"]}><NurseVerification /></ProtectedRoute>} />

          {/* Dashboards */}
          <Route path="/nurse/dashboard" element={<ProtectedRoute roles={["nurse"]}><NurseDashboard /></ProtectedRoute>} />
          <Route path="/nurse/benefits" element={<ProtectedRoute roles={["nurse"]}><NurseBenefits /></ProtectedRoute>} />
          <Route path="/nurse/timesheet" element={<ProtectedRoute roles={["nurse"]}><NurseTimesheet /></ProtectedRoute>} />
          <Route path="/nurse/compliance" element={<ProtectedRoute roles={["nurse"]}><NurseCompliance /></ProtectedRoute>} />
          <Route path="/client/dashboard" element={<ProtectedRoute roles={["client"]}><ClientDashboard /></ProtectedRoute>} />
          <Route path="/admin/dashboard" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />

          {/* Admin Pages */}
          <Route path="/admin/shifts" element={<ProtectedRoute roles={["admin"]}><AdminShifts /></ProtectedRoute>} />
          <Route path="/admin/nurses" element={<ProtectedRoute roles={["admin"]}><AdminNurses /></ProtectedRoute>} />
          <Route path="/admin/nurses-manage" element={<ProtectedRoute roles={["admin"]}><AdminNursesManage /></ProtectedRoute>} />
          <Route path="/admin/clients" element={<ProtectedRoute roles={["admin"]}><AdminClients /></ProtectedRoute>} />
          <Route path="/admin/clients-manage" element={<ProtectedRoute roles={["admin"]}><AdminClientsManage /></ProtectedRoute>} />
          <Route path="/admin/compliance" element={<ProtectedRoute roles={["admin"]}><AdminCompliance /></ProtectedRoute>} />
          <Route path="/admin/jobs" element={<ProtectedRoute roles={["admin"]}><AdminJobs /></ProtectedRoute>} />

          {/* Future placeholders */}
          <Route path="/services" element={<Index />} />
          <Route path="/resources" element={<Resources />} />
          
          {/* Articles */}
          <Route path="/articles/falls-prevention" element={<FallsPrevention />} />
          <Route path="/articles/understanding-dementia" element={<UnderstandingDementia />} />
          <Route path="/articles/nutrition-healthy-aging" element={<NutritionHealthyAging />} />
          <Route path="/articles/childhood-asthma" element={<ChildhoodAsthma />} />
          <Route path="/articles/mental-health-awareness" element={<MentalHealthAwareness />} />
          <Route path="/articles/meal-planning" element={<MealPlanning />} />
          <Route path="/articles/exercise-seniors" element={<ExerciseSeniors />} />
          <Route path="/articles/childrens-emotional-development" element={<ChildrensEmotionalDevelopment />} />
          <Route path="/articles/managing-stress-anxiety" element={<ManagingStressAnxiety />} />

          {/* Catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        <ConsentBanner />
      </BrowserRouter>
  </HelmetProvider>
);

export default App;
